import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useConference } from '@/context/ConferenceContext';
import { submitContact, getErrorMessage } from '@/services/api';

const contactSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Invalid email address'),
  subject: z.string().min(5, 'Subject is required'),
  message: z.string().min(20, 'Message must be at least 20 characters'),
});

const ContactPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { loginDetails, importantDetails, siteConfig, shortName, getConferenceVenue, getImportantDates } = useConference();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    try {
      const contactData = {
        ...data,
        user: shortName, // Add the conference short name as user
      };
      await submitContact(contactData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      reset();
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - {siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Get in touch with the CCAI-2026 organizing committee" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Contact Us</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Have questions? We're here to help
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info & Form */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Information */}
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="text-sky-500" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                        <a
                          href={`mailto:${loginDetails?.email || siteConfig?.contact?.email || 'info@conference.com'}`}
                          className="text-gray-600 hover:text-sky-500 text-sm"
                        >
                          {loginDetails?.email || siteConfig?.contact?.email || 'info@conference.com'}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="text-sky-500" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                        <a
                          href={`tel:${siteConfig?.contact?.phone || '+33 1 40 68 22 22'}`}
                          className="text-gray-600 hover:text-sky-500 text-sm"
                        >
                          {siteConfig?.contact?.phone || '+33 1 40 68 22 22'}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-sky-500" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                        <p className="text-gray-600 text-sm">{siteConfig?.contact?.address || 'Conference Secretariat, Palais des Congrès de Paris, 2 Place de la Porte Maillot, 75017 Paris, France'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-sky-50 p-6 rounded-2xl">
                  <h3 className="font-semibold text-gray-900 mb-3">Office Hours</h3>
                  <p className="text-gray-700 text-sm">
                    Monday - Friday: 9:00 AM - 5:00 PM (PST)
                    <br />
                    Saturday - Sunday: Closed
                  </p>
                  <p className="text-gray-600 text-xs mt-3">
                    We typically respond to inquiries within 24-48 hours.
                  </p>
                </div>

                {loginDetails && (
                  <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-3">Conference Details</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      {loginDetails.ConfName && <p><strong>Name:</strong> {loginDetails.ConfName}</p>}
                      {loginDetails.conference_url && <p><strong>URL:</strong> <a href={loginDetails.conference_url} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">{loginDetails.conference_url}</a></p>}
                      {loginDetails.username && <p><strong>Username:</strong> {loginDetails.username}</p>}
                    </div>
                  </div>
                )}

                {importantDetails && (
                  <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-3">Important Information</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      {importantDetails.ConferenceTitle && <p><strong>Title:</strong> <span dangerouslySetInnerHTML={{ __html: importantDetails.ConferenceTitle }} /></p>}
                      {getConferenceVenue() && <p><strong>Venue:</strong> {getConferenceVenue()}</p>}
                      {importantDetails.ConferenceDates && <p><strong>Dates:</strong> {importantDetails.ConferenceDates}</p>}
                      {(() => {
                        const dates = getImportantDates();
                        return (
                          <>
                            {dates.abstract_submission_deadline && <p><strong>Abstract Deadline:</strong> {dates.abstract_submission_deadline}</p>}
                            {dates.registration_opens && <p><strong>Registration Opens:</strong> {dates.registration_opens}</p>}
                            {dates.EarlyBird && <p><strong>Early Bird:</strong> {dates.EarlyBird}</p>}
                            {dates.mid_term && <p><strong>Mid Term:</strong> {dates.mid_term}</p>}
                            {dates.OnSpot && <p><strong>On Spot:</strong> {dates.OnSpot}</p>}
                          </>
                        );
                      })()}
                      {importantDetails.Late_registration && <p><strong>Late Registration:</strong> {importantDetails.Late_registration}</p>}
                    </div>
                    
                    {(importantDetails.facebook_link || importantDetails.linkedin_link || importantDetails.instagram_link || importantDetails.twitter_link) && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Social Media</h4>
                        <div className="flex flex-wrap gap-2">
                          {importantDetails.facebook_link && (
                            <a href={importantDetails.facebook_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Facebook</a>
                          )}
                          {importantDetails.linkedin_link && (
                            <a href={importantDetails.linkedin_link} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline text-xs">LinkedIn</a>
                          )}
                          {importantDetails.instagram_link && (
                            <a href={importantDetails.instagram_link} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline text-xs">Instagram</a>
                          )}
                          {importantDetails.twitter_link && (
                            <a href={importantDetails.twitter_link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-xs">Twitter</a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="bg-white p-6 md:p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                  
                  {isSubmitted ? (
                    <div className="text-center py-12" data-testid="success-message">
                      <div className="inline-flex w-16 h-16 rounded-full bg-green-100 items-center justify-center mb-4">
                        <Send className="text-green-500" size={32} />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                      <p className="text-gray-600">
                        Thank you for contacting us. We'll get back to you soon.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            {...register('name')}
                            placeholder="Your name"
                            className="mt-1"
                            data-testid="name-input"
                          />
                          {errors.name && (
                            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            {...register('email')}
                            placeholder="your.email@example.com"
                            className="mt-1"
                            data-testid="email-input"
                          />
                          {errors.email && (
                            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          {...register('subject')}
                          placeholder="What is this regarding?"
                          className="mt-1"
                          data-testid="subject-input"
                        />
                        {errors.subject && (
                          <p className="text-red-500 text-sm mt-1">{errors.subject.message}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          {...register('message')}
                          placeholder="Tell us more about your inquiry..."
                          className="mt-1 min-h-[150px]"
                          data-testid="message-textarea"
                        />
                        {errors.message && (
                          <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-sky-500 hover:bg-sky-600 py-3"
                        data-testid="submit-contact-btn"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
